// src/pages/business/BusinessBookingTypes.jsx
import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  Tab,
  Tabs,
  useTheme,
  IconButton,
  Box,
  Paper,
  Button,
  Divider,
  FormControl,
  Link as MuiLink,
  Skeleton,
  // 👇 NEW: dialog imports
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  PhoneAndroid,
  AccessTime,
  Email,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Web,
  Close as CloseIcon, // 👈 NEW
} from "@mui/icons-material";
import TimezoneSelector from "../../../../../platform/util/timezoneSelector";
import { GetTimezoneInfo } from "../../../../../platform/util/timezones";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { GetSortedWorkingHours } from "../../../../../platform/util/workingHour";
import BusinessBookingTypeList from "./businessBookingTypeList";
import { GET_PUBLIC_BOOKING_BUSINESS_BY_URL } from "../../../../../platform/graphql/queries";
import BookingTypeStyle from "../../../../../platform/components/business/business/businessBookingType.styles";
import officePlaceHolderImage from "../../../../../platform/assets/images/business-image-placeholder.png";
import BusinessAddressLink from "../../../../../platform/util/google/businessAddressLink";
import SeoMeta from "../../../../../platform/components/common/seoMeta";

// Fix Leaflet marker icons (Webpack/CRA)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const SocialIconsMap = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: LinkedIn,
  youtube: YouTube,
  website: Web,
};

export const BusinessBookingTypes = () => {
  const { providerUser } = useContext(UserContext);
  const theme = useTheme();
  const styles = BookingTypeStyle(theme);
  const { businessUrl } = useParams();

  const [selectedTab, setSelectedTab] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedTimeZone, setSelectedTimeZone] = useState(
    GetTimezoneInfo(providerUser?.timezone)?.timezone
  );

  // 👇 NEW: gallery dialog state
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const openGallery = () => setIsGalleryOpen(true);
  const closeGallery = () => setIsGalleryOpen(false);

  const {
    loading: loadingBusiness,
    error: errorBusiness,
    data: dataBusiness,
  } = useQuery(GET_PUBLIC_BOOKING_BUSINESS_BY_URL, {
    variables: { businessUrl },
    skip: !businessUrl,
  });

  const business = useMemo(() => {
    if (!dataBusiness?.businessByUrl) return null;
    return {
      ...dataBusiness.businessByUrl,
      businessBasic: dataBusiness.businessByUrl,
      url: businessUrl,
    };
  }, [dataBusiness, businessUrl]);

  const handleTimezoneSelect = useCallback((tz) => setSelectedTimeZone(tz), []);
  const handleBack = () => window.history.back();
  const handleTabChange = (_e, v) => setSelectedTab(v);

  if (loadingBusiness) {
    return (
      <Container maxWidth="lg" sx={styles.root}>
        <Box sx={styles.backLink}>
          <FaChevronLeft style={styles.backIcon} />
          <Typography variant="subtitle1">Back</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" sx={styles.imageContainer} />
            <Skeleton variant="text" sx={{ mt: 2, width: "40%" }} />
            <Skeleton variant="rounded" sx={{ height: 120, mt: 1 }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={220} />
            <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
            <Skeleton variant="rounded" height={220} sx={{ mt: 2 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (errorBusiness) {
    return (
      <Container maxWidth="lg" sx={styles.root}>
        <Typography color="error">Error: {errorBusiness.message}</Typography>
      </Container>
    );
  }

  if (!business) return null;

  const isAFHBusiness = Boolean(business?.careSettings?.afhLicenseNumber);
  const businessName = business?.businessBasic?.name || business?.name || "Timely365";
  const businessDescription =
    business?.businessBasic?.description ||
    (isAFHBusiness
      ? `View care details and availability for ${businessName}.`
      : `Explore public services and events available at ${businessName}.`);

  const sampleImages =
    (business.images || [])
      .filter((it) => Number(it?.businessId) === Number(business.id))
      .map((it) => it?.url) ?? [];

  const handlePreviousImage = () =>
    setCurrentImageIndex((i) => (i === 0 ? sampleImages.length - 1 : i - 1));
  const handleNextImage = () =>
    setCurrentImageIndex((i) => (i === sampleImages.length - 1 ? 0 : i + 1));

  const workingHourGroup =
    business.workingHourGroups.find((g) => g.def === true) ||
    business.workingHourGroups[0];

  const sortedWorkingHours = GetSortedWorkingHours(
    workingHourGroup?.workingHours || [],
    selectedTimeZone ?? workingHourGroup?.timeZone
  );

  const formatPhoneNumber = (n) => {
    const cleaned = ("" + n).replace(/\D/g, "");
    const m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
  };

  // 👇 NEW: styles for the overlay button (kept inline to avoid editing style file)
  const viewAllBtnSx = {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 2,
    bgcolor: "rgba(0,0,0,0.45)",
    color: "#fff",
    "&:hover": { bgcolor: "rgba(0,0,0,0.65)" },
    textTransform: "none",
    px: 1.25,
    py: 0.5,
    borderRadius: 2,
    fontSize: 13,
  };

  return (
    <Container maxWidth="lg" sx={styles.root}>
      <SeoMeta
        title={`${businessName} | Timely365`}
        description={businessDescription}
        image={sampleImages[0] || officePlaceHolderImage}
      />
      {/* Back link */}
      <Box sx={styles.backLink} onClick={handleBack} aria-label="Go back">
        <FaChevronLeft style={styles.backIcon} />
        <Typography variant="subtitle1">Back</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* LEFT: Media + Booking Types */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={styles.imageContainer}>
            <Box sx={styles.imageWrapper}>
              {/* 👇 NEW: View all photos overlay */}
              {sampleImages.length > 0 && (
                <Button
                  size="small"
                  onClick={openGallery}
                  sx={viewAllBtnSx}
                  aria-label="View all photos"
                >
                  View all photos ({sampleImages.length})
                </Button>
              )}

              {sampleImages.length > 1 && (
                <IconButton
                  onClick={handlePreviousImage}
                  sx={styles.imageNavButtonLeft}
                  aria-label="Previous image"
                >
                  <FaChevronLeft />
                </IconButton>
              )}

              <Box
                component="img"
                src={
                  sampleImages.length
                    ? sampleImages[currentImageIndex]
                    : officePlaceHolderImage
                }
                alt={
                  sampleImages.length
                    ? `Business image ${currentImageIndex + 1} of ${sampleImages.length}`
                    : "Business placeholder"
                }
                sx={styles.businessImage}
                loading="lazy"
                onClick={sampleImages.length ? openGallery : undefined}
                style={{ cursor: sampleImages.length ? "zoom-in" : "default" }}
              />

              {sampleImages.length > 1 && (
                <IconButton
                  onClick={handleNextImage}
                  sx={styles.imageNavButtonRight}
                  aria-label="Next image"
                >
                  <FaChevronRight />
                </IconButton>
              )}
            </Box>
          </Paper>

          {isAFHBusiness ? (
            <Box sx={styles.tabsContainer}>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                <Tab label="Booking Types" />
              </Tabs>
            </Box>
          ) : (
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Services & Events
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Public services and events currently available for booking.
              </Typography>
            </Box>
          )}

          <Box>
            <BusinessBookingTypeList
              business={business}
              url={business?.businessBasic?.url ?? business?.url}
            />
          </Box>
        </Grid>

        {/* RIGHT: Details / Contact / Hours */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={styles.businessDetails}>
            <Typography variant="h5" component="h1" sx={styles.sectionTitle}>
              {business.businessBasic.name}
            </Typography>
            {!isAFHBusiness && business.workspaceType !== "PERSONAL" && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Business Overview
              </Typography>
            )}

            {business.businessBasic.description && (
              <Typography variant="body1" color="text.secondary">
                {business.businessBasic.description}
              </Typography>
            )}

            {business.businessBasic.address && (
              <Typography variant="body2" color="text.secondary">
                <BusinessAddressLink address={business.businessBasic.address} />
              </Typography>
            )}
          </Paper>

          <Paper elevation={3} sx={styles.aboutUs}>
            <Typography variant="h6" sx={styles.sectionTitle} gutterBottom>
              Contact Us
            </Typography>

            {/* Phones */}
            {(business.businessBasic.phoneNumbers || []).map((phone, i) => (
              <Box
                key={`phone-${i}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneAndroid fontSize="small" />
                  <Typography variant="body1">
                    {formatPhoneNumber(phone.phoneNumber)}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() =>
                    (window.location.href = `tel:${phone.phoneNumber}`)
                  }
                >
                  Call
                </Button>
              </Box>
            ))}

            {/* Emails */}
            {(business.businessBasic.emails || []).map((email, i) => (
              <Box
                key={`email-${i}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Email fontSize="small" />
                  <Typography variant="body1">{email}</Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => (window.location.href = `mailto:${email}`)}
                >
                  Email
                </Button>
              </Box>
            ))}

            {/* Social */}
            {business.businessBasic.links?.length > 0 && (
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                mt={0.5}
                flexWrap="wrap"
              >
                {business.businessBasic.links.map(({ platform, url }) => {
                  const IconComponent =
                    SocialIconsMap[platform] || SocialIconsMap.website;
                  return (
                    <MuiLink
                      key={`${platform}-${url}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "text.primary",
                        "&:hover": { color: "primary.main" },
                      }}
                      aria-label={platform}
                    >
                      <IconComponent />
                    </MuiLink>
                  );
                })}
              </Box>
            )}
          </Paper>

          <Paper elevation={3} sx={styles.workingHours}>
            <Typography variant="h6" sx={styles.sectionTitle} gutterBottom>
              {isAFHBusiness ? "Working Hours" : "Availability"}
            </Typography>

            <FormControl fullWidth sx={{ mb: 1.5 }}>
              <TimezoneSelector
                timezoneId={selectedTimeZone ?? workingHourGroup?.timeZone}
                onTimezoneSelect={handleTimezoneSelect}
              />
            </FormControl>

            {sortedWorkingHours.map((hour) => {
              const parentHour = hour.parent;
              const childrenHour = hour.children;
              if (!parentHour) return null;

              return (
                <Box key={parentHour.id}>
                  <Typography
                    variant="body1"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTime fontSize="small" />
                      {parentHour.day}
                    </Box>
                    <Box>
                      {parentHour.onJob
                        ? `${parentHour.startTime} - ${parentHour.endTime}`
                        : "Closed"}
                    </Box>
                  </Typography>

                  {childrenHour.map((child) => (
                    <Typography
                      key={child.id}
                      variant="body2"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pl: 4,
                        py: 0.25,
                        color: "text.secondary",
                      }}
                    >
                      <span>{child.day}</span>
                      <span>
                        {child.onJob
                          ? `${child.startTime} - ${child.endTime}`
                          : "Closed"}
                      </span>
                    </Typography>
                  ))}

                  <Divider sx={{ my: 1 }} />
                </Box>
              );
            })}
          </Paper>
        </Grid>
      </Grid>

      {/* 👇 NEW: Gallery Dialog */}
      {/* Gallery Dialog — responsive: 1/2/3/4 columns, non-clickable */}
      <Dialog
        open={isGalleryOpen}
        onClose={closeGallery}
        fullWidth
        maxWidth="xl"
        aria-labelledby="gallery-title"
      >
        <DialogTitle
          id="gallery-title"
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <Typography variant="h6">
            Photos {sampleImages.length ? `(${sampleImages.length})` : ""}
          </Typography>
          <IconButton onClick={closeGallery} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* 1 col (phone), 2 (tablet), 3 (small desktop), 4 (large) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",  // 👈 one per row on phones
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            {(sampleImages.length ? sampleImages : [officePlaceHolderImage]).map((src, idx) => (
              <Box
                key={`${src}-${idx}`}
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  // Taller on phones, classic 4:3 above
                  aspectRatio: { xs: "16 / 9", sm: "4 / 3" },
                  boxShadow: 1,
                  cursor: "default", // non-clickable
                }}
                aria-label={`Photo ${idx + 1}`}
              >
                <Box
                  component="img"
                  src={src}
                  alt={`Gallery item ${idx + 1}`}
                  loading="lazy"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeGallery}>Close</Button>
        </DialogActions>
      </Dialog>


    </Container>
  );
};

export default BusinessBookingTypes;
